/***
 * Created on 2024/10/30
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/

import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from "react-i18next";
import TextInputComponent from "../common/Form/TextInputComponent";
import SelectInputComponent from "../common/Form/SelectInputComponent";
import TextInputWithCopyComponent from "../common/Form/TextInputWithCopyComponent";
import ic_remove_svg from '../../static/img/utils/ic-remove.svg'
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useRegion} from "../../context/RegionContext";
import {useAlertContext} from "../../context/AlertContext";
import {useOutletContext} from "react-router-dom";

const init_input = {
    login_enforcement: {value: 'Optional', error: null, required: true, init_value: ''},
    idp_identifier: {value: '', error: null, required: true, init_value: ''},
    connection_type: {value: 'SAML', error: null, required: true, init_value: ''},
    identity_provider: {value: 'Microsoft Azure Ad', error: null, required: true, init_value: ''},
    idp_signin_url: {value: '', error: null, required: true, init_value: ''},
    idp_signout_url: {value: '', error: null, required: false, init_value: ''},
    certificate: {value: '', error: null, required: true, init_value: ''},
    organization_identifier: {value: '', error: null, required: true, init_value: ''}
}

const LOGIN_ENFORCEMENT_LIST = ['Optional', 'Required'];
const CONNECTION_TYPE_LIST = ['SAML', 'OPENID']
const IDENTITY_PROVIDER_LIST = ['Google', 'Microsoft Azure Ad', 'Okta', 'Others'];
const LOGIN_ENFORCEMENT_NOTES = new Map([
    ['Optional', '@Your members can log in to QNAP services using SSO or their QID password.@'],
    ['Required', '@Your members must log in to QNAP Account using SSO, and their QID password will no longer work.@']
]);

const VALID_HTTPS_URL_FORMAT = /^(https:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/i;
const VALID_HTTPS_URL_OR_EMPTY_FORMAT = /^((https:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?|)$/i;

function SettingsAuthentication({organization_id, organization}) {
    const {userPermissions} = useOutletContext();
    const hasPermission = userPermissions.hasPermission('manage_organization_settings');
    const { t } = useTranslation();
    const [input, setInput] = useState(structuredClone(init_input));
    const [configSSO, setConfigSSO] = useState(false);
    const [enableSwitch, setEnableSwitch] = useState(organization?.enable_sso && hasPermission);
    const uploadFile = useRef();
    const {OrganizationAPI} = useOrganizationApi();
    const [certificateExpired, setCertificateExpired] = useState(false);
    const [pkiCertificate, setPkiCertificate] = useState(null);
    const {url} = useRegion();
    const queryClient = useQueryClient();
    const alert = useAlertContext();

    const onOpenFileUpload = () => {
        uploadFile.current.click();
    }

    const onFileChange = (e) => {
        e.stopPropagation();
        e.preventDefault();
        const file = uploadFile.current.files[0];
        setInput(prev => {
            return {
                ...prev,
                certificate: {
                    ...prev.certificate,
                    value: file
                }
            }
        });
    }

    const {data, error, } = useQuery({
        queryKey: ['get_org_sso', organization_id],
        queryFn: async ()=> await OrganizationAPI.get(`organization/${organization_id}/sso`),
        onSuccess: data => {
            setConfigSSO(true);
            setCertificateExpired(data?.result?.idp?.certificate_expires);
            setInput(prev => {
                prev['idp_identifier'].value = data?.result?.idp?.entity_id;
                prev['idp_signin_url'].value = data?.result?.idp?.sso;
                if (data?.result.idp?.slo) {
                    prev['idp_signout_url'].value = data?.result.idp?.slo;
                }
                return prev;
            });
            setPkiCertificate(data?.result?.certificate);
            return data;

            // setEnableSwitch(true);
        }
    })
    if (error) {
        // setEnableSwitch(organization?.enable_sso);
    }

    const removeCertificate = () => {
        setCertificateExpired(null);
    }

    const saveMutate = useMutation({
        mutationFn: async (data) => await OrganizationAPI.upload(`organization/${organization_id}/sso`, data?.data, data?.method),
        onSuccess: (data) => {
            alert(true, 'success', 0, t('@Success@'));
            queryClient.invalidateQueries({
                queryKey: ['get_org_sso', organization_id]
            });
        },
        onError: (error, variables, context) => {
            console.log(error);
        }
    })

    const onSave = async () => {
        const method = data?.result?.organization_id ? 'put' : 'post';
        const certificate = input?.certificate?.value
        const idp = {
            entity_id: input.idp_identifier.value,
            sso: input.idp_signin_url.value,
        }
        if (certificate) {
            idp.certificate = certificate;
        }
        await saveMutate.mutateAsync({
            data: {
                type: input.connection_type.value,
                organization_identifier: input.organization_identifier.value,
                login_enforcement: input.login_enforcement.value,
                idp
            },
            method
        });
    };

    const renewMutate = useMutation({
        mutationFn: async (data) => await OrganizationAPI.put(`organization/${organization_id}/sso/pki`),
        onSuccess: data => {
            setPkiCertificate(data?.result?.certificate);
        }
    });

    const onRenew = async () => {
        await renewMutate.mutateAsync(null);
    }

    const deleteMutate = useMutation({
        mutationFn: async () => await OrganizationAPI.delete(`organization/${organization_id}/sso`),
        onSuccess: data1 => {
            queryClient.setQueryData('get_org_sso', null);
            queryClient.invalidateQueries({
                queryKey: ['get_org_sso']
            });
            setCertificateExpired(null);
        }
    });

    const onDelete = async () => {
        await deleteMutate.mutateAsync(null);
    }

    const switchMutate = useMutation({
        mutationFn: async (data) => await OrganizationAPI.post(`organization/${organization_id}/sso/switch/${data}`),
        onSuccess: data => {
            queryClient.invalidateQueries({
                queryKey: [`organization/${organization_id}`]
            });
        }
    });

    const onToggleSwitch = async () => {
        if (!hasPermission) {
            // alert(true, 'error', 0, t('@You do not have permission to perform this action.@'));
            return;
        }
        const operation = enableSwitch ? 'off' : 'on';
        setEnableSwitch(prev=> !prev);
        try {
            await switchMutate.mutateAsync(operation);
        } catch (error) {
            setEnableSwitch(prev=> !prev);
            alert(true, 'error', error?.response?.data?.code, error?.response?.data?.message);
        }
    }

    const onDownload = () => {
        const blob = new Blob([pkiCertificate], {type: "application/x-x509-ca-cert"});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.download = 'certificate.pem';
        a.href = url;
        a.click();
    }

    const checkValidation = useMemo(() => {
        for (const [_, item] of Object.entries(input)) {
            if (item?.error?.length > 0 || (item.required && !item?.value)) {
                if (_ === 'certificate' && certificateExpired) {}
                else{
                    return true;
                }
            }
        }
        return false;
    }, [ input.organization_identifier.value, input.idp_identifier.value, input.idp_signin_url.value, input.idp_signout_url.value, input.certificate.value, certificateExpired]);

    useEffect(() => {
        setInput(prev => {
            return {
                ...prev,
                organization_identifier: {
                    ...prev.organization_identifier,
                    value: organization?.hash_code
                }
            }
        })
    }, [organization?.hash_code]);

    return (
        <>
            <div className="content-wrapper page-wrapper !pl-0 !pr-0">
                <div className="content-inner">
                    <div className="content-col-right">
                        <div className="form-group-wrapper">
                            <div className="form-row form-row--head form-row--sticky-top">
                                <div className="form-title">{t('@Authentication@')}</div>
                            </div>

                            <div className="form-row">
                                <div className="form-label"><label>{t('@Single sign-on@')}</label></div>
                                <div className="form-field">
                                    <div className="display-flex mt-8">
                                        <div className="comp-switch" onClick={onToggleSwitch}>
                                            <div className={`q-switch switch-${enableSwitch ? 'on' : 'off'}`}></div>
                                        </div>
                                    </div>
                                    <div className="note-msg"></div>
                                </div>
                            </div>

                            <div className="form-group" data-form-group-state={enableSwitch ? 'unlock' : 'lock'}>
                                <div className="form-group-inner">
                                    <SelectInputComponent label={t('@Login Enforcement@')} field_name={'login_enforcement'}
                                                          input={input} setInput={setInput}
                                                          listData={LOGIN_ENFORCEMENT_LIST}
                                                          useLocale={false}
                                                          empty_text={t('@Please select a Identity Provider@')}
                                                          disabled={false}
                                                          show_note={true}
                                                          note_message={LOGIN_ENFORCEMENT_NOTES}
                                    />

                                    <SelectInputComponent label={t('@Identity Provider@')} field_name={'identity_provider'}
                                                          input={input} setInput={setInput}
                                                          listData={IDENTITY_PROVIDER_LIST}
                                                          useLocale={false}
                                                          empty_text={t('@Please select a Identity Provider@')}
                                                          disabled={false}
                                    />

                                    <TextInputComponent label={t('@IdP identifier@')}
                                                        field_name={'idp_identifier'}
                                                        input={input} setInput={setInput}
                                                        place_holder={t('@The unique identifier for the IdP@')}
                                                        foramtError={t('@Punctuation marks cannot be used in the first character@')}>
                                    </TextInputComponent>

                                    <SelectInputComponent label={t('@Connection type@')} field_name={'connection_type'}
                                                          input={input} setInput={setInput}
                                                          listData={CONNECTION_TYPE_LIST}
                                                          useLocale={false}
                                                          empty_text={t('@Please select a connection type@')}
                                                          disabled={true}
                                    />

                                    <TextInputComponent label={t('@IdP Sign-in URL@')}
                                                        field_name={'idp_signin_url'}
                                                        input={input} setInput={setInput}
                                                        place_holder={t('@Provided by your IdP.@')}
                                                        format={VALID_HTTPS_URL_FORMAT}
                                                        foramtError={t('@Please enter a valid url@')}>
                                    </TextInputComponent>

                                    <TextInputComponent label={t('@IdP Sign-out URL@')}
                                                        field_name={'idp_signout_url'}
                                                        input={input} setInput={setInput}
                                                        place_holder={t('@Provided by your IdP.@')}
                                                        format={VALID_HTTPS_URL_OR_EMPTY_FORMAT}
                                                        foramtError={t('@Please enter a valid url@')}>
                                    </TextInputComponent>

                                    <div className="form-row">
                                        <div className="form-label required">
                                            <label>{t('@X.509 Certificate@')}</label>
                                            <span className="fs13 color-gray">{t('@(PEM / CER format)@')}</span>
                                        </div>
                                        <div className="form-field">
                                            {((!input?.certificate?.value || input.certificate.value instanceof File) && !certificateExpired) &&
                                                <div className="mf-group mf-group--browse !pr-8">
                                                    <div className="mf-group__text">{`${input?.certificate?.value?.name ? input.certificate.value.name : t('@Attach file@')}`}</div>
                                                    <div className="mf-group__divide"></div>
                                                    <div className="mf-group__action">
                                                        <button type="button" onClick={onOpenFileUpload}
                                                                className="q-btn q-btn-default q-btn-small q-btn-round-corner">
                                                            <span>{t('@Browse@')}</span>
                                                        </button>
                                                        <input type='file' ref={uploadFile} className='hide' accept=".cer,.pem"
                                                               onChange={(e) => onFileChange(e)}/>
                                                    </div>
                                                </div>
                                            }

                                            {certificateExpired &&
                                                <div className="mf-group mf-group--browse">
                                                    <div className="mf-group__text">
                                                        <div className="mb-4">X.509 Certificate* Certificate.pem</div>
                                                        <div className="fs13 color-dark-gray">{t('@Expires@')}: {certificateExpired?.split('T')[0]}</div>
                                                    </div>
                                                    <div className="mf-group__divide"></div>
                                                    <div className="mf-group__action" onClick={removeCertificate}>
                                                        <div className="util-btn">
                                                            <img src={ic_remove_svg} className="!pt-11 !pr-11 !pb-11 !pl-11" alt=""/>
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>

                                    <div className="form-row form-row--head">
                                        <div className="form-title mt-16">{t('@Service Provider Information@')}</div>
                                    </div>

                                    <TextInputWithCopyComponent
                                        label={t('@Organization Identifier@')}
                                        field_name={'organization_identifier'}
                                        input={input} setInput={setInput}/>

                                    <TextInputWithCopyComponent
                                        label={t('@Entity ID@')}
                                        field_name={'spi_entity_id'}
                                        input={{spi_entity_id: {value: `${url.get_acc_url()}/saml/org/${input?.organization_identifier?.value}`}}} setInput={() => {
                                    }} disabled={true}/>

                                    <TextInputWithCopyComponent
                                        label={t('@Callback URL@')}
                                        field_name={'spi_callback_url'}
                                        input={{spi_callback_url: {value: `${url.get_acc_url()}/saml/callback`}}} setInput={() => {
                                    }} disabled={true}/>

                                    <TextInputWithCopyComponent
                                        label={t('@Audience@')}
                                        field_name={'spi_audience'}
                                        input={{spi_audience: {value: `${url.get_acc_url()}/saml/org/${input?.organization_identifier?.value}`}}} setInput={() => {
                                    }} disabled={true}/>

                                    <div className="form-row">
                                        <div className="form-label"><label>{t('@SAML Response Signing Certificate@')}</label></div>
                                        <div className="form-field">
                                            <div className="q-btn-group mt-8">
                                                <button type="button" onClick={onDownload} className="q-btn q-btn-default q-btn-tiny q-btn-rounded-full">
                                                    {t('@Download Certificate@')}
                                                    <a href=""></a>
                                                </button>
                                                <button type="button" onClick={onRenew} className="q-btn q-btn-default q-btn-tiny q-btn-rounded-full">
                                                    {t('@Regenerate@')}
                                                </button>
                                            </div>
                                            <div className="note-msg">{t('@Used to sign SAML responses for secure authentication between your IdP and QNAP service.@')}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="content-footer">
                <div className="q-btn-group">
                    <button type="button" className="q-btn q-btn-default" onClick={onSave} disabled={checkValidation}>{t('@Save Connection@')}</button>
                    <button type="button" className="q-btn q-btn-default q-btn-danger" onClick={onDelete} disabled={!data?.result.organization_id}>{t('@Delete SSO Configuration@')}</button>
                </div>
            </div>
        </>
    );
}

export default SettingsAuthentication;